import { call, put, select } from "redux-saga/effects";
import {
  DISPATCH_TYPE,
  LISTS,
  LIST_ACTIONS,
  IMPORT_STATUS
} from "../utils/enums.ts";
import {
  guestClientTypeProperties,
  guestCombinationProperties,
  guestFAQProperties,
  guestQuestionTypeProperties,
  guestSectionProperties,
  guestKeywordProperties
} from "../utils/ListProperties.ts";
import * as spAPI from "../utils/sharepointAPI.ts";
import { EnvironmentPaths, getEnvironmentPaths } from "../utils/utils.ts";
import { buildSelectQuery } from "../utils/SpUtility.ts";
import { DateTime } from "luxon";
import { RootState } from "@/reducers/rootReducer.ts";
import {
  IClientType,
  IClientTypeGuest,
  ICombination,
  ICombinationGuest,
  IFAQ,
  IFAQGuest,
  IImportDate,
  IKeyword,
  IKeywordGuest,
  IQuestionType,
  IQuestionTypeGuest,
  ISection,
  ISectionGuest
} from "@/types/SPTypes.tsx";
import { getImportFileDataActionType } from "@/actionTypes/importActionTypes.ts";
import { takeEvery } from "redux-saga/effects";
import {
  NewILSClientData,
  NewILSClientDataToSend,
  NewILSCombinationsData,
  NewILSCombinationsDataToSend,
  NewILSFAQData,
  NewILSFAQDataToSend,
  NewILSKeywordsData,
  NewILSKeywordsDataToSend,
  NewILSQuestionTypesData,
  NewILSQuestionTypesDataToSend,
  NewILSSectionData
} from "@/types/importFileDataTypes.ts";

export function* initializeFileImport(action: getImportFileDataActionType) {
  // Activates the disabled-overlay as file-import starts
  document
    .getElementById("disabled-overlay")
    ?.style.setProperty("display", "block");
  const importDate: IImportDate[] | null = yield select(
    (state: RootState) => state.importData.importDate
  );

  const env = getEnvironmentPaths();

  try {
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_STATUS,
      payload: IMPORT_STATUS.IMPORTING
    });

    const { listapi } = env;

    const clientTypesURL =
      listapi +
      "/getByTitle('ClientTypes')/Items" +
      buildSelectQuery(guestClientTypeProperties);

    const combinationsURL =
      listapi +
      "/getByTitle('Combinations')/Items" +
      buildSelectQuery(guestCombinationProperties);

    const FAQURL =
      listapi +
      "/getByTitle('FAQs')/Items" +
      buildSelectQuery(guestFAQProperties);

    const keywordsURL =
      listapi +
      "/getByTitle('Keywords')/Items" +
      buildSelectQuery(guestKeywordProperties);

    const sectionsURL =
      listapi +
      "/getByTitle('Sections')/Items" +
      buildSelectQuery(guestSectionProperties);

    const questionTypesURL =
      listapi +
      "/getByTitle('QuestionTypes')/Items" +
      buildSelectQuery(guestQuestionTypeProperties);

    const fileData = action.payload;

    // Sections
    // SP Sections List GET
    const ILSSectionsData: ISectionGuest[] = yield call(() =>
      spAPI.get(sectionsURL)
    );
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_SECTIONS,
      payload: ILSSectionsData
    });
    // VSDL Sections from file import
    const VSDLSectionsData = fileData.sections;

    yield call(sectionSort, ILSSectionsData, VSDLSectionsData, env);

    // ClientTypes
    // SP ILS FAQ ClientTypes List GET
    const ILSClientTypesData: IClientTypeGuest[] = yield call(() =>
      spAPI.get(clientTypesURL)
    );
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_CLIENT_TYPES,
      payload: ILSClientTypesData
    });
    // VSDL ClientTypes from file import
    const VSDLClientTypesData = fileData.clientTypes;

    yield call(clientTypesSort, ILSClientTypesData, VSDLClientTypesData, env);

    // Combinations
    // SP ILS FAQ Combinations List GET
    const ILSCombinationsData: ICombinationGuest[] = yield call(() =>
      spAPI.get(combinationsURL)
    );
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_COMBINATIONS,
      payload: ILSCombinationsData
    });
    // VSDL Combinations from file import
    const VSDLCombinationsData = fileData.combinations;

    yield call(
      combinationsSort,
      ILSCombinationsData,
      VSDLCombinationsData,
      env
    );

    // QuestionTypes
    // SP QuestionTypes List GET
    const ILSQuestionTypesData: IQuestionTypeGuest[] = yield call(() =>
      spAPI.get(questionTypesURL)
    );
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_QUESTION_TYPES,
      payload: ILSQuestionTypesData
    });
    // VSDL QuestionTypes from file import
    const VSDLQuestionTypesData = fileData.questionTypes;

    yield call(
      questionTypesSort,
      ILSQuestionTypesData,
      VSDLQuestionTypesData,
      env
    );

    // Keywords
    // SP Keywords List GET
    const ILSKeywordsData: IKeywordGuest[] = yield call(() =>
      spAPI.get(keywordsURL)
    );
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_KEYWORDS,
      payload: ILSKeywordsData
    });
    // VSDL Keywords from file import
    const VSDLKeywordsData = fileData.keywords;

    yield call(keyWordsSort, ILSKeywordsData, VSDLKeywordsData, env);

    // FAQs
    // SP ILS FAQs List GET
    const ILSFAQsData: IFAQGuest[] = yield call(() => spAPI.get(FAQURL));
    yield put({ type: DISPATCH_TYPE.SET_IMPORT_FAQS, payload: ILSFAQsData });
    // VSDL FAQs from file import
    const VSDLFAQsData = fileData.FAQs;

    yield call(FAQsSort, ILSFAQsData, VSDLFAQsData, env);

    yield call(updateImportDateSaga, importDate, env);

    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_STATUS,
      payload: IMPORT_STATUS.DONE
    });
    yield put({
      type: DISPATCH_TYPE.GET_INITIAL_DATA
    });
  } catch (error) {
    yield put({
      type: DISPATCH_TYPE.SET_IMPORT_STATUS,
      payload: IMPORT_STATUS.ERROR
    });
    console.log("Error in importFileDataSaga - initializeFileImport() ", error);
  }

  // Disables the disabled-overlay
  const overlay = document.getElementById("disabled-overlay");
  if (overlay) {
    overlay.style.display = "none";
  }
}

function* clientTypesSort(
  ILSClientTypesData?: IClientTypeGuest[],
  VSDLClientTypesData?: IClientType[],
  env?: EnvironmentPaths
) {
  if (!ILSClientTypesData || !VSDLClientTypesData || !env) {
    return;
  }

  // Create a new array to store updated ILSClientTypesData
  let updatedILSClientTypesData: NewILSClientData[] = ILSClientTypesData.map(
    clientType => ({
      ID: clientType.ID,
      Modified: clientType.Modified,
      clientTypeTitle: clientType.clientTypeTitle,
      vsdlId: clientType.vsdlId,
      action: LIST_ACTIONS.UPDATE
    })
  );

  for (const VSDLClientTypesItem of VSDLClientTypesData) {
    const existingItemIndex = updatedILSClientTypesData.findIndex(
      clientType => clientType.vsdlId === VSDLClientTypesItem.ID
    );

    if (existingItemIndex !== -1) {
      const existingItem = updatedILSClientTypesData[existingItemIndex];
      const isModified =
        Date.parse(VSDLClientTypesItem?.Modified ?? "") >
        Date.parse(existingItem?.Modified ?? "");

      updatedILSClientTypesData[existingItemIndex] = {
        ...existingItem,
        clientTypeTitle: VSDLClientTypesItem.clientTypeTitle,
        vsdlId: VSDLClientTypesItem.ID,
        action: isModified ? LIST_ACTIONS.UPDATE : LIST_ACTIONS.NONE
      };
    } else {
      // Handle new client types
      const newILSClientTypesItem: NewILSClientData = {
        action: LIST_ACTIONS.NEW,
        clientTypeTitle: VSDLClientTypesItem.clientTypeTitle,
        vsdlId: VSDLClientTypesItem.ID
      };

      updatedILSClientTypesData = [
        ...updatedILSClientTypesData,
        newILSClientTypesItem
      ];
    }
  }

  for (const clientTypeItem of updatedILSClientTypesData) {
    const dataToPost: NewILSClientDataToSend = {
      clientTypeTitle: clientTypeItem.clientTypeTitle,
      Title: clientTypeItem.clientTypeTitle,
      vsdlId: clientTypeItem.vsdlId,
      __metadata: { type: "SP.Data." + LISTS.CLIENT_TYPES + "ListItem" }
    };

    switch (clientTypeItem.action) {
      case LIST_ACTIONS.UPDATE:
        try {
          if (!clientTypeItem.ID) {
            throw new Error("NO ID!");
          }

          const updateURL = `${env.listapi}/getByTitle('${LISTS.CLIENT_TYPES}')/Items(${clientTypeItem.ID})`;
          yield spAPI.update(updateURL, dataToPost);
        } catch (error) {
          console.log("CLIENT_TYPES UPDATE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NEW:
        try {
          const postURL = `${env.listapi}/getByTitle('${LISTS.CLIENT_TYPES}')/Items`;
          yield spAPI.save(postURL, dataToPost);
        } catch (error) {
          console.log("CLIENT_TYPES NEW ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NONE:
        console.log("INSIDE NONE ACTION IF");
        // Skip API call
        break;
      default:
        console.log("clientTypesSort() switch default körs!");
        break;
    }
  }
}

function* combinationsSort(
  ILSCombinationsData?: ICombinationGuest[],
  VSDLCombinationsData?: ICombination[],
  env?: EnvironmentPaths
) {
  if (!ILSCombinationsData || !VSDLCombinationsData || !env) {
    return;
  }

  // Create a new array to store updated ILSCombinationsData
  let updatedILSCombinationsData: NewILSCombinationsData[] =
    ILSCombinationsData.map(combination => ({
      ID: combination.ID,
      Modified: combination.Modified,
      clientTypeTitle: combination.clientTypeTitle,
      questionTypeTitle: combination.questionTypeTitle,
      sectionTitle: combination.sectionTitle,
      vsdlId: combination.vsdlId,
      action: LIST_ACTIONS.UPDATE
    }));

  for (const VSDLCombinationsItem of VSDLCombinationsData) {
    const existingItemIndex = updatedILSCombinationsData.findIndex(
      combination => combination.vsdlId === VSDLCombinationsItem.ID
    );

    if (existingItemIndex !== -1) {
      const existingItem = updatedILSCombinationsData[existingItemIndex];
      const isModified =
        Date.parse(VSDLCombinationsItem?.Modified ?? "") >
        Date.parse(existingItem?.Modified ?? "");

      updatedILSCombinationsData[existingItemIndex] = {
        ...existingItem,
        clientTypeTitle: VSDLCombinationsItem.clientType.clientTypeTitle,
        questionTypeTitle: VSDLCombinationsItem.questionType.questionTitle,
        sectionTitle: VSDLCombinationsItem.section.sectionTitle,
        vsdlId: VSDLCombinationsItem.ID,
        action: isModified ? LIST_ACTIONS.UPDATE : LIST_ACTIONS.NONE
      };
    } else {
      // Handle new combinations
      const newILSCombinationsItem: NewILSCombinationsData = {
        clientTypeTitle: VSDLCombinationsItem.clientType.clientTypeTitle,
        questionTypeTitle: VSDLCombinationsItem.questionType.questionTitle,
        sectionTitle: VSDLCombinationsItem.section.sectionTitle,
        vsdlId: VSDLCombinationsItem.ID,
        action: LIST_ACTIONS.NEW
      };

      updatedILSCombinationsData = [
        ...updatedILSCombinationsData,
        newILSCombinationsItem
      ];
    }
  }

  for (const combinationsItem of updatedILSCombinationsData) {
    const dataToPost: NewILSCombinationsDataToSend = {
      clientTypeTitle: combinationsItem.clientTypeTitle,
      questionTypeTitle: combinationsItem.questionTypeTitle,
      sectionTitle: combinationsItem.sectionTitle,
      vsdlId: combinationsItem.vsdlId,
      __metadata: { type: "SP.Data." + LISTS.COMBINATIONS + "ListItem" }
    };

    switch (combinationsItem.action) {
      case LIST_ACTIONS.UPDATE:
        try {
          if (!combinationsItem.ID) {
            throw new Error("NO ID!");
          }

          const updateURL = `${env.listapi}/getByTitle('${LISTS.COMBINATIONS}')/Items(${combinationsItem.ID})`;
          yield spAPI.update(updateURL, dataToPost);
        } catch (error) {
          console.log("COMBINATIONS UPDATE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NEW:
        try {
          const postURL = `${env.listapi}/getByTitle('${LISTS.COMBINATIONS}')/Items`;
          yield spAPI.save(postURL, dataToPost);
        } catch (error) {
          console.log("COMBINATIONS SAVE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NONE:
        // Skip API call
        break;
      default:
        console.log("combinationsSort() switch default körs!");
        break;
    }
  }
}

function* FAQsSort(
  ILSFAQsData?: IFAQGuest[],
  VSDLFAQData?: IFAQ[],
  env?: EnvironmentPaths
) {
  if (!ILSFAQsData || !VSDLFAQData || !env) {
    return;
  }

  // Create a new array to store updated ILSFAQsData
  let updatedILSFAQsData: NewILSFAQData[] = ILSFAQsData.map(faq => ({
    ID: faq.ID,
    Modified: faq.Modified,
    FAQTitle: faq.FAQTitle,
    FAQ_Id: faq.ID.toString(),
    answer: faq.answer,
    sectionTitle: faq.sectionTitle,
    questionTypeTitle: faq.questionTypeTitle,
    clientTypesTitle: faq.clientTypesTitle,
    question: faq.question,
    reference: faq.reference,
    verifiedDate: faq.verifiedDate,
    vsdlId: faq.vsdlId,
    active: false,
    action: LIST_ACTIONS.UPDATE
  }));

  for (const VSDLFAQItem of VSDLFAQData) {
    const existingItemIndex = updatedILSFAQsData.findIndex(
      FAQ => FAQ.vsdlId === VSDLFAQItem.ID
    );

    if (existingItemIndex !== -1) {
      const existingItem = updatedILSFAQsData[existingItemIndex];
      const isModified =
        Date.parse(VSDLFAQItem?.Modified ?? "") >
        Date.parse(existingItem?.Modified ?? "");

      updatedILSFAQsData[existingItemIndex] = {
        ...existingItem,
        active: VSDLFAQItem.active,
        FAQTitle: VSDLFAQItem.FAQTitle ?? "",
        FAQ_Id: VSDLFAQItem?.ID.toString() ?? undefined,
        answer: VSDLFAQItem.answer ?? "",
        sectionTitle: VSDLFAQItem.section.sectionTitle,
        questionTypeTitle: VSDLFAQItem.questionType.questionTitle,
        clientTypesTitle: VSDLFAQItem.clientTypes.map(
          clientType => clientType.clientTypeTitle
        )[0],
        question: VSDLFAQItem.question ?? "",
        reference: VSDLFAQItem.reference ?? "",
        vsdlId: VSDLFAQItem.ID ?? null,
        action: isModified ? LIST_ACTIONS.UPDATE : LIST_ACTIONS.NONE
      };
    } else {
      // Handle new FAQs
      const newILSFAQItem: NewILSFAQData = {
        active: VSDLFAQItem.active,
        FAQTitle: VSDLFAQItem.FAQTitle,
        FAQ_Id: VSDLFAQItem.ID.toString(),
        answer: VSDLFAQItem.answer,
        sectionTitle: VSDLFAQItem.section.sectionTitle,
        questionTypeTitle: VSDLFAQItem.questionType.questionTitle,
        clientTypesTitle: VSDLFAQItem.clientTypes.map(
          clientType => clientType.clientTypeTitle
        )[0],
        question: VSDLFAQItem.question,
        reference: VSDLFAQItem.reference,
        verifiedDate: VSDLFAQItem.verifiedDate,
        vsdlId: VSDLFAQItem.ID,
        action: LIST_ACTIONS.NEW
      };

      updatedILSFAQsData = [...updatedILSFAQsData, newILSFAQItem];
    }
  }

  for (const faqItem of updatedILSFAQsData) {
    const dataToPost: NewILSFAQDataToSend = {
      active: faqItem.active,
      FAQTitle: faqItem.FAQTitle,
      FAQ_Id: faqItem?.FAQ_Id,
      answer: faqItem.answer,
      vsdlId: faqItem.vsdlId,
      sectionTitle: faqItem.sectionTitle,
      questionTypeTitle: faqItem.questionTypeTitle,
      clientTypesTitle: faqItem.clientTypesTitle,
      Title: faqItem.FAQTitle,
      question: faqItem.question,
      reference: faqItem.reference,
      verifiedDate: faqItem.verifiedDate,
      __metadata: { type: "SP.Data." + LISTS.FAQS + "ListItem" }
    };

    switch (faqItem.action) {
      case LIST_ACTIONS.UPDATE:
        try {
          if (!faqItem.ID) {
            throw new Error("NO ID!");
          }

          const updateURL = `${env.listapi}/getByTitle('${LISTS.FAQS}')/Items(${faqItem.ID})`;
          yield spAPI.update(updateURL, dataToPost);
        } catch (error) {
          console.log("FAQs UPDATE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NEW:
        try {
          const postURL = `${env.listapi}/getByTitle('${LISTS.FAQS}')/Items`;
          yield spAPI.save(postURL, dataToPost);
        } catch (error) {
          console.log("FAQs SAVE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NONE:
        // Skip API call
        break;
      default:
        console.log("FAQsSort() switch default körs!");
        break;
    }
  }
}

function* keyWordsSort(
  ILSKeywordsData?: IKeywordGuest[],
  VSDLKeywordsData?: IKeyword[],
  env?: EnvironmentPaths
) {
  if (!ILSKeywordsData || !VSDLKeywordsData || !env) {
    return;
  }

  // Create a new array to store updated ILSKeywordsData
  let updatedILSKeywordsData: NewILSKeywordsData[] = ILSKeywordsData.map(
    keyword => ({
      ID: keyword.ID,
      Modified: keyword.Modified,
      keyword: keyword.keyword,
      sectionTitle: keyword.sectionTitle,
      vsdlId: keyword.vsdlId,
      active: false,
      action: LIST_ACTIONS.UPDATE
    })
  );

  for (const VSDLKeywordsItem of VSDLKeywordsData) {
    const existingItemIndex = updatedILSKeywordsData.findIndex(
      keyword => keyword.vsdlId === VSDLKeywordsItem.ID
    );

    if (existingItemIndex !== -1) {
      const existingItem = updatedILSKeywordsData[existingItemIndex];
      const isModified =
        Date.parse(VSDLKeywordsItem?.Modified ?? "") >
        Date.parse(existingItem?.Modified ?? "");

      updatedILSKeywordsData[existingItemIndex] = {
        ...existingItem,
        active: true,
        keyword: VSDLKeywordsItem.keyword,
        sectionTitle: VSDLKeywordsItem.section.sectionTitle,
        vsdlId: VSDLKeywordsItem.ID,
        action: isModified ? LIST_ACTIONS.UPDATE : LIST_ACTIONS.NONE
      };
    } else {
      // Handle new keywords
      const newILSKeywordsItem: NewILSKeywordsData = {
        active: true,
        keyword: VSDLKeywordsItem.keyword,
        sectionTitle: VSDLKeywordsItem.section.sectionTitle,
        vsdlId: VSDLKeywordsItem.ID,
        action: LIST_ACTIONS.NEW
      };

      updatedILSKeywordsData = [...updatedILSKeywordsData, newILSKeywordsItem];
    }
  }

  for (const keyWordsItem of updatedILSKeywordsData) {
    const dataToPost: NewILSKeywordsDataToSend = {
      active: keyWordsItem.active,
      keyword: keyWordsItem.keyword,
      sectionTitle: keyWordsItem.sectionTitle,
      vsdlId: keyWordsItem.vsdlId,
      Title: keyWordsItem.keyword,
      __metadata: { type: "SP.Data." + LISTS.KEYWORDS + "ListItem" }
    };

    switch (keyWordsItem.action) {
      case LIST_ACTIONS.UPDATE:
        try {
          if (!keyWordsItem.ID) {
            throw new Error("NO ID!");
          }

          const updateURL = `${env.listapi}/getByTitle('${LISTS.KEYWORDS}')/Items(${keyWordsItem.ID})`;
          yield spAPI.update(updateURL, dataToPost);
        } catch (error) {
          console.log("KEYWORDS UPDATE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NEW:
        try {
          const postURL = `${env.listapi}/getByTitle('${LISTS.KEYWORDS}')/Items`;
          yield spAPI.save(postURL, dataToPost);
        } catch (error) {
          console.log("KEYWORDS SAVE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NONE:
        // Skip API call
        break;
      default:
        console.log("keyWordsSort() switch default körs!");
        break;
    }
  }
}

function* sectionSort(
  ILSSectionsData?: ISectionGuest[],
  VSDLSectionsData?: ISection[],
  env?: EnvironmentPaths
) {
  if (!ILSSectionsData || !VSDLSectionsData || !env) {
    return;
  }
  // Create a new array to store updated ILSSectionsData
  let updatedILSSectionsData: NewILSSectionData[] = ILSSectionsData.map(
    section => ({
      ID: section.ID,
      Modified: section.Modified,
      sectionTitle: section.sectionTitle,
      sectionType: section.sectionType,
      connectedSectionsTitles: section.connectedSectionsTitles,
      vsdlId: section.vsdlId,
      active: false,
      action: LIST_ACTIONS.UPDATE
    })
  );

  for (const VSDLSectionItem of VSDLSectionsData) {
    const existingItemIndex = updatedILSSectionsData.findIndex(
      section => section.vsdlId === VSDLSectionItem.ID
    );

    // adding the connected sections ID's in an array
    // Since we can't have a lookup, connectedSectionsTitles is a string separated by commas

    const connectedSections =
      Array.isArray(VSDLSectionItem?.connectedSections) &&
      VSDLSectionItem?.connectedSections?.length > 0
        ? VSDLSectionItem.connectedSections
            .map(connectedSection => connectedSection.sectionTitle)
            .filter((title: string) => title !== undefined && title !== null)
            .join(", ")
        : "";

    if (existingItemIndex !== -1) {
      const existingItem = updatedILSSectionsData[existingItemIndex];
      const isModified =
        Date.parse(VSDLSectionItem?.Modified ?? "") >
        Date.parse(existingItem?.Modified ?? "");

      updatedILSSectionsData[existingItemIndex] = {
        ...existingItem,
        active: true,
        sectionTitle: VSDLSectionItem.sectionTitle,
        sectionType: VSDLSectionItem.sectionType,
        vsdlId: VSDLSectionItem.ID,
        connectedSectionsTitles: connectedSections,
        action: isModified ? LIST_ACTIONS.UPDATE : LIST_ACTIONS.NONE
      };
    } else {
      // Handle new sections
      const newILSSectionItem = {
        active: true,
        sectionTitle: VSDLSectionItem.sectionTitle ?? "",
        sectionType: VSDLSectionItem.sectionType,
        vsdlId: VSDLSectionItem.ID,
        connectedSectionsTitles: connectedSections,
        action: LIST_ACTIONS.NEW
      };

      updatedILSSectionsData = [...updatedILSSectionsData, newILSSectionItem];
    }
  }

  for (const sectionItem of updatedILSSectionsData) {
    const dataToPost = {
      active: sectionItem.active,
      sectionTitle: sectionItem.sectionTitle ?? "",
      sectionType: sectionItem.sectionType,
      vsdlId: sectionItem.vsdlId,
      connectedSectionsTitles: sectionItem.connectedSectionsTitles,
      Title: sectionItem.sectionTitle ?? "",
      __metadata: { type: "SP.Data." + LISTS.SECTIONS + "ListItem" }
    };

    switch (sectionItem.action) {
      case LIST_ACTIONS.UPDATE:
        try {
          if (!sectionItem.ID) {
            throw new Error("NO ID!");
          }

          const updateURL = `${env.listapi}/getByTitle('${LISTS.SECTIONS}')/Items(${sectionItem.ID})`;
          yield spAPI.update(updateURL, dataToPost);
        } catch (error) {
          console.log("SECTIONS UPDATE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NEW:
        try {
          const postURL = `${env.listapi}/getByTitle('${LISTS.SECTIONS}')/Items`;
          yield spAPI.save(postURL, dataToPost);
        } catch (error) {
          console.log("SECTIONS SAVE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NONE:
        // Skip API call
        break;
      default:
        console.log("sectionSort() switch default körs!");
        break;
    }
  }
}

function* questionTypesSort(
  ILSQuestionTypesData?: IQuestionTypeGuest[],
  VSDLQuestionTypesData?: IQuestionType[],
  env?: EnvironmentPaths
) {
  if (!ILSQuestionTypesData || !VSDLQuestionTypesData || !env) {
    return;
  }

  // Create a new array to store updated ILSQuestionTypesData
  let updatedILSQuestionTypesData: NewILSQuestionTypesData[] =
    ILSQuestionTypesData.map(questionType => ({
      ID: questionType.ID,
      Modified: questionType.Modified,
      questionDescription: questionType.questionDescription,
      questionTitle: questionType.questionTitle,
      questionTemplate: questionType.questionTemplate,
      vsdlId: questionType.vsdlId,
      action: LIST_ACTIONS.UPDATE
    }));

  for (const VSDLQuestionTypeItem of VSDLQuestionTypesData) {
    const existingItemIndex = updatedILSQuestionTypesData.findIndex(
      questionType => questionType.vsdlId === VSDLQuestionTypeItem.ID
    );

    if (existingItemIndex !== -1) {
      const existingItem = updatedILSQuestionTypesData[existingItemIndex];
      const isModified =
        Date.parse(VSDLQuestionTypeItem?.Modified ?? "") >
        Date.parse(existingItem?.Modified ?? "");

      updatedILSQuestionTypesData[existingItemIndex] = {
        ...existingItem,
        questionDescription: VSDLQuestionTypeItem.questionDescription,
        questionTitle: VSDLQuestionTypeItem.questionTitle,
        questionTemplate: VSDLQuestionTypeItem.questionTemplate,
        vsdlId: VSDLQuestionTypeItem.ID,
        action: isModified ? LIST_ACTIONS.UPDATE : LIST_ACTIONS.NONE
      };
    } else {
      // Handle new question types
      const newILSQuestionTypesItem: NewILSQuestionTypesData = {
        questionDescription: VSDLQuestionTypeItem.questionDescription,
        questionTitle: VSDLQuestionTypeItem.questionTitle,
        questionTemplate: VSDLQuestionTypeItem.questionTemplate,
        vsdlId: VSDLQuestionTypeItem.ID,
        action: LIST_ACTIONS.NEW
      };

      updatedILSQuestionTypesData = [
        ...updatedILSQuestionTypesData,
        newILSQuestionTypesItem
      ];
    }
  }

  for (const questionTypesItem of updatedILSQuestionTypesData) {
    const dataToPost: NewILSQuestionTypesDataToSend = {
      questionDescription: questionTypesItem.questionDescription,
      questionTitle: questionTypesItem.questionTitle,
      questionTemplate: questionTypesItem.questionTemplate,
      vsdlId: questionTypesItem.vsdlId,
      __metadata: { type: "SP.Data." + LISTS.QUESTION_TYPES + "ListItem" }
    };

    switch (questionTypesItem.action) {
      case LIST_ACTIONS.UPDATE:
        try {
          if (!questionTypesItem.ID) {
            throw new Error("NO ID!");
          }

          const updateURL = `${env.listapi}/getByTitle('${LISTS.QUESTION_TYPES}')/Items(${questionTypesItem.ID})`;
          yield spAPI.update(updateURL, dataToPost);
        } catch (error) {
          console.log("QUESTION_TYPES UPDATE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NEW:
        try {
          const postURL = `${env.listapi}/getByTitle('${LISTS.QUESTION_TYPES}')/Items`;
          yield spAPI.save(postURL, dataToPost);
        } catch (error) {
          console.log("QUESTION_TYPES SAVE ERROR: ", error);
        }
        break;
      case LIST_ACTIONS.NONE:
        // Skip API call
        break;
      default:
        console.log("questionTypesSort() switch default körs!");
        break;
    }
  }
}

function* updateImportDateSaga(
  importDate?: IImportDate[] | null,
  env?: EnvironmentPaths
) {
  if (!importDate || !env) {
    return;
  }

  try {
    if (!importDate[0]) {
      const typeString = "SP.Data." + LISTS.IMPORT_DATE + "ListItem";
      const newImportDate = {
        date: DateTime.now().toUTC().toISO(),
        __metadata: { type: typeString }
      };
      const postURL = `${env.listapi}/getByTitle('${LISTS.IMPORT_DATE}')/Items`;
      yield spAPI.save(postURL, newImportDate);
    } else {
      const typeString = "SP.Data." + LISTS.IMPORT_DATE + "ListItem";
      const updateImportDate = {
        date: DateTime.now().toUTC().toISO(),
        __metadata: { type: typeString }
      };
      const updateURL = `${env.listapi}/getByTitle('${LISTS.IMPORT_DATE}')/Items(${importDate[0].ID})`;
      yield spAPI.update(updateURL, updateImportDate);
    }
  } catch (error) {
    console.log("Update import date", error);
  }
}

// Saga middle-ware
// Catches dispatched data
export function* importFileDataSaga() {
  yield takeEvery(DISPATCH_TYPE.GET_IMPORT_FILE_DATA, initializeFileImport);
}

export default importFileDataSaga;
